import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { PublicShell, Loading } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { LearnCard, HANDLE } from "@/components/learn/common";
import { useHead } from "@/lib/useHead";
import api from "@/lib/api";

const TABS = [["", "All"], ["episode", "Episodes"], ["carousel", "Carousels"], ["guide", "Guides"]];

export default function Learn() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const tag = params.get("tag") || "";
  const q = params.get("q") || "";
  const [text, setText] = useState(q);
  const set = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  useEffect(() => { const t = setTimeout(() => { if (text !== q) set("q", text.trim()); }, 350); return () => clearTimeout(t); }); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["learn", type, tag, q], initialPageParam: 1,
    queryFn: async ({ pageParam }) => (await api.get("/learn", { params: { type, tag, q, page: pageParam } })).data,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
  });
  const items = data?.pages.flatMap((p) => p.items) || [];
  const tags = data?.pages[0]?.tags || [];
  useHead({ title: `Learn with ${HANDLE} — Shubham Bapna`, description: "Short episodes, carousels and free guides on business finance, marketing, ecommerce and AI for Indian business owners.", url: `${window.location.origin}/learn` });

  return (
    <PublicShell>
      <div className="mx-auto max-w-[1100px] px-5 py-14 lg:px-8" data-testid="learn-page">
        <p className="kicker text-xs text-accent">Learn with {HANDLE}</p>
        <h1 className="mt-3 font-heading text-[clamp(2rem,6vw,3rem)] font-extrabold leading-tight text-ink">Making AI easy and profitable for business owners.</h1>
        <p className="mt-3 max-w-2xl text-muted">Every episode, carousel and guide from {HANDLE}, in one place — business finance, marketing, ecommerce and AI.</p>

        <div className="mt-8 flex flex-col gap-4 border-b border-border pb-5 md:flex-row md:items-center md:justify-between">
          <div className="-mx-1 flex gap-1 overflow-x-auto px-1" role="tablist" data-testid="learn-tabs">
            {TABS.map(([v, label]) => (
              <button key={label} role="tab" aria-selected={type === v} onClick={() => set("type", v)} data-testid={`learn-tab-${v || "all"}`}
                className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${type === v ? "bg-ink text-white" : "text-muted hover:bg-card hover:text-ink"}`}>{label}</button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search episodes, guides…" className="h-10 pl-9" data-testid="learn-search" />
          </div>
        </div>

        {tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="learn-tags">
            {tags.map((t) => (
              <button key={t} onClick={() => set("tag", tag === t ? "" : t)} data-testid={`learn-tag-${t}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${tag === t ? "border-accent bg-accent text-white" : "border-border text-muted hover:border-accent hover:text-accent"}`}>#{t}</button>
            ))}
          </div>
        )}

        {isLoading ? <Loading /> : items.length === 0 ? (
          <div className="mt-10 rounded-[10px] border border-border bg-card p-8" data-testid="learn-empty">
            <p className="font-heading text-lg font-bold text-ink">{q || tag || type ? "Nothing matches that yet." : "The first episodes are on their way."}</p>
            <p className="mt-1 text-sm text-muted">{q || tag || type ? "Try another filter or clear the search." : "New episodes and carousels appear here the moment they're posted."}</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="learn-grid">
            {items.map((it) => <LearnCard key={it.id} item={it} />)}
          </div>
        )}
        {hasNextPage && (
          <div className="mt-8 text-center">
            <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} data-testid="learn-load-more" className="rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-ink hover:bg-card">
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
